import { useState, useEffect, useCallback, useRef } from 'react';
import { Layout } from '../components/Layout';
import { BarChart3, Calendar, Users, ShoppingCart, Package, RefreshCw, X, Search, Tag } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { formatISTDate } from '../utils/timezone';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const TIME_RANGE_OPTIONS = [
  { value: 'today', label: 'Today' },
  { value: 'this_week', label: 'This Week' },
  { value: 'this_month', label: 'This Month' },
  { value: 'this_year', label: 'This Year' },
  { value: 'custom', label: 'Custom Range' },
];

const STATUS_OPTIONS = [
  { value: 'all', label: 'All Statuses' },
  { value: 'draft', label: 'Draft' },
  { value: 'finalized', label: 'Finalized' },
  { value: 'dispatched', label: 'Dispatched' },
  { value: 'cancelled', label: 'Cancelled' },
];

const getStatusBadge = (status) => {
  const colors = {
    draft: 'bg-slate-100 text-slate-700 border-slate-200',
    finalized: 'bg-emerald-50 text-emerald-600 border-emerald-200',
    dispatched: 'bg-blue-50 text-blue-600 border-blue-200',
    cancelled: 'bg-rose-50 text-rose-600 border-rose-200',
  };
  return (
    <span className={`px-2 py-1 text-xs font-medium rounded-full border ${colors[status] || colors.draft}`}>
      {status?.charAt(0).toUpperCase() + status?.slice(1)}
    </span>
  );
};

const formatCurrency = (amount) => {
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format(amount);
};

export const Reports = () => {
  const [loading, setLoading] = useState(true);
  const [customers, setCustomers] = useState([]);
  const [reportData, setReportData] = useState(null);
  
  // Filter state
  const [timeRange, setTimeRange] = useState('this_month');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [selectedCustomer, setSelectedCustomer] = useState('all');
  const [selectedStatus, setSelectedStatus] = useState('all');
  
  // Design number filter state
  const [designSearch, setDesignSearch] = useState('');
  const [designSuggestions, setDesignSuggestions] = useState([]);
  const [selectedDesigns, setSelectedDesigns] = useState([]);
  const [showDesignDropdown, setShowDesignDropdown] = useState(false);
  const [loadingDesigns, setLoadingDesigns] = useState(false);
  const designInputRef = useRef(null);
  const dropdownRef = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowDesignDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Fetch design suggestions
  const fetchDesignSuggestions = useCallback(async (search) => {
    if (!search.trim()) {
      setDesignSuggestions([]);
      return;
    }
    setLoadingDesigns(true);
    try {
      const response = await axios.get(`${API}/reports/design-numbers?search=${encodeURIComponent(search)}`);
      // Filter out already selected designs
      const filtered = response.data.filter(
        d => !selectedDesigns.some(sd => sd.design_number === d.design_number)
      );
      setDesignSuggestions(filtered);
    } catch (error) {
      console.error('Failed to fetch design suggestions:', error);
    } finally {
      setLoadingDesigns(false);
    }
  }, [selectedDesigns]);

  // Debounce design search
  useEffect(() => {
    const timer = setTimeout(() => {
      if (designSearch) {
        fetchDesignSuggestions(designSearch);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [designSearch, fetchDesignSuggestions]);

  // Add design to selected list
  const addDesign = (design) => {
    setSelectedDesigns(prev => [...prev, design]);
    setDesignSearch('');
    setDesignSuggestions([]);
    setShowDesignDropdown(false);
  };

  // Remove design from selected list
  const removeDesign = (designNumber) => {
    setSelectedDesigns(prev => prev.filter(d => d.design_number !== designNumber));
  };

  // Fetch customers for filter dropdown
  const fetchCustomers = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/reports/customers`);
      // Ensure customers is always an array
      const customersData = Array.isArray(response.data) ? response.data : [];
      setCustomers(customersData);
    } catch (error) {
      console.error('Failed to fetch customers:', error);
      setCustomers([]); // Ensure customers is always an array
    }
  }, []);

  // Fetch report data
  const fetchReport = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.append('time_range', timeRange);
      
      if (timeRange === 'custom') {
        if (fromDate) params.append('from_date', new Date(fromDate).toISOString());
        if (toDate) params.append('to_date', new Date(toDate).toISOString());
      }
      
      if (selectedCustomer !== 'all') {
        params.append('customer_email', selectedCustomer);
      }
      
      if (selectedStatus !== 'all') {
        params.append('status', selectedStatus);
      }
      
      // Add design numbers filter
      if (selectedDesigns.length > 0) {
        params.append('design_numbers', selectedDesigns.map(d => d.design_number).join(','));
      }
      
      const response = await axios.get(`${API}/reports?${params.toString()}`);
      setReportData(response.data);
    } catch (error) {
      toast.error('Failed to fetch report data');
      console.error('Failed to fetch report:', error);
    } finally {
      setLoading(false);
    }
  }, [timeRange, fromDate, toDate, selectedCustomer, selectedStatus, selectedDesigns]);

  useEffect(() => {
    fetchCustomers();
  }, [fetchCustomers]);

  useEffect(() => {
    fetchReport();
  }, [fetchReport]);

  const handleResetFilters = () => {
    setTimeRange('this_month');
    setFromDate('');
    setToDate('');
    setSelectedCustomer('all');
    setSelectedStatus('all');
    setSelectedDesigns([]);
    setDesignSearch('');
  };

  const hasActiveFilters = timeRange !== 'this_month' || selectedCustomer !== 'all' || selectedStatus !== 'all' || selectedDesigns.length > 0;

  return (
    <Layout>
      <div className="space-y-4 sm:space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4">
          <div>
            <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-slate-900 tracking-tight" style={{ fontFamily: 'Outfit, sans-serif' }} data-testid="reports-title">
              Reports
            </h1>
            <p className="mt-1 text-sm sm:text-base lg:text-lg text-slate-600" style={{ fontFamily: 'Public Sans, sans-serif' }}>
              Analyze order data and track business performance
            </p>
          </div>
          <button
            onClick={fetchReport}
            disabled={loading}
            className="inline-flex items-center justify-center w-full sm:w-auto px-4 py-2.5 bg-slate-900 text-white rounded-md hover:bg-slate-800 font-medium transition-all active:scale-[0.98] disabled:opacity-50 text-sm sm:text-base"
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        </div>

        {/* Filters */}
        <div className="bg-white border border-slate-200 rounded-lg shadow-sm p-4 sm:p-6">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-4">
            <h2 className="text-lg font-semibold text-slate-900 flex items-center gap-2">
              <BarChart3 className="w-5 h-5 text-indigo-600" />
              Filters
            </h2>
            {hasActiveFilters && (
              <button
                onClick={handleResetFilters}
                className="inline-flex items-center text-sm text-slate-600 hover:text-slate-900 transition-colors"
              >
                <X className="w-4 h-4 mr-1" />
                Reset Filters
              </button>
            )}
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {/* Time Range Filter */}
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1.5">
                <Calendar className="w-4 h-4 inline mr-1.5" />
                Time Range
              </label>
              <select
                value={timeRange}
                onChange={(e) => setTimeRange(e.target.value)}
                data-testid="time-range-filter"
                className="w-full px-3 py-2.5 border border-slate-200 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 text-sm bg-white"
              >
                {TIME_RANGE_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>

            {/* Custom Date Range */}
            {timeRange === 'custom' && (
              <>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1.5">From Date</label>
                  <input
                    type="date"
                    value={fromDate}
                    onChange={(e) => setFromDate(e.target.value)}
                    data-testid="from-date-filter"
                    className="w-full px-3 py-2.5 border border-slate-200 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 text-sm"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1.5">To Date</label>
                  <input
                    type="date"
                    value={toDate}
                    onChange={(e) => setToDate(e.target.value)}
                    data-testid="to-date-filter"
                    className="w-full px-3 py-2.5 border border-slate-200 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 text-sm"
                  />
                </div>
              </>
            )}

            {/* Customer Filter */}
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1.5">
                <Users className="w-4 h-4 inline mr-1.5" />
                Customer
              </label>
              <select
                value={selectedCustomer}
                onChange={(e) => setSelectedCustomer(e.target.value)}
                data-testid="customer-filter"
                className="w-full px-3 py-2.5 border border-slate-200 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 text-sm bg-white"
              >
                <option value="all">All Customers</option>
                {Array.isArray(customers) && customers.map((customer) => (
                  <option key={customer?.email || Math.random()} value={customer?.email || ''}>
                    {customer?.name || 'Unknown'} ({customer?.email || 'N/A'})
                  </option>
                ))}
              </select>
            </div>

            {/* Status Filter */}
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1.5">
                <ShoppingCart className="w-4 h-4 inline mr-1.5" />
                Order Status
              </label>
              <select
                value={selectedStatus}
                onChange={(e) => setSelectedStatus(e.target.value)}
                data-testid="status-filter"
                className="w-full px-3 py-2.5 border border-slate-200 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 text-sm bg-white"
              >
                {STATUS_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>

            {/* Design Number Filter */}
            <div className="lg:col-span-2" ref={dropdownRef}>
              <label className="block text-sm font-medium text-slate-700 mb-1.5">
                <Tag className="w-4 h-4 inline mr-1.5" />
                Design Number
              </label>
              <div className="relative">
                <div className="flex items-center gap-2">
                  <div className="relative flex-1">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <input
                      ref={designInputRef}
                      type="text"
                      placeholder="Search design number..."
                      value={designSearch}
                      onChange={(e) => {
                        setDesignSearch(e.target.value);
                        setShowDesignDropdown(true);
                      }}
                      onFocus={() => setShowDesignDropdown(true)}
                      data-testid="design-number-filter"
                      className="w-full pl-9 pr-4 py-2.5 border border-slate-200 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 text-sm"
                    />
                  </div>
                </div>
                
                {/* Dropdown suggestions */}
                {showDesignDropdown && designSearch && (
                  <div className="absolute z-10 w-full mt-1 bg-white border border-slate-200 rounded-md shadow-lg max-h-60 overflow-y-auto">
                    {loadingDesigns ? (
                      <div className="px-4 py-3 text-sm text-slate-500">Searching...</div>
                    ) : designSuggestions.length === 0 ? (
                      <div className="px-4 py-3 text-sm text-slate-500">No matching designs found</div>
                    ) : (
                      designSuggestions.map((design) => (
                        <button
                          key={design.design_number}
                          type="button"
                          onClick={() => addDesign(design)}
                          className="w-full px-4 py-2.5 text-left hover:bg-slate-50 transition-colors border-b border-slate-100 last:border-b-0"
                        >
                          <span className="font-medium text-slate-900">{design.design_number}</span>
                          <span className="text-sm text-slate-500 ml-2">{design.product_name}</span>
                        </button>
                      ))
                    )}
                  </div>
                )}
              </div>
              
              {/* Selected designs tags */}
              {selectedDesigns.length > 0 && (
                <div className="flex flex-wrap gap-2 mt-2">
                  {selectedDesigns.map((design) => (
                    <span
                      key={design.design_number}
                      className="inline-flex items-center gap-1 px-2.5 py-1 bg-indigo-50 text-indigo-700 rounded-full text-sm font-medium"
                    >
                      {design.design_number}
                      <button
                        type="button"
                        onClick={() => removeDesign(design.design_number)}
                        className="hover:bg-indigo-100 rounded-full p-0.5 transition-colors"
                      >
                        <X className="w-3.5 h-3.5" />
                      </button>
                    </span>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div className="bg-white border border-slate-200 rounded-lg shadow-sm p-4 sm:p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-slate-500">Total Order Value</p>
                <p className="text-2xl sm:text-3xl font-bold text-slate-900 mt-1" data-testid="total-value">
                  {loading ? '...' : formatCurrency(reportData?.total_order_value || 0)}
                </p>
              </div>
              <div className="p-3 bg-emerald-50 rounded-lg">
                <BarChart3 className="w-6 h-6 text-emerald-600" />
              </div>
            </div>
          </div>

          <div className="bg-white border border-slate-200 rounded-lg shadow-sm p-4 sm:p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-slate-500">Total Orders</p>
                <p className="text-2xl sm:text-3xl font-bold text-slate-900 mt-1" data-testid="total-orders">
                  {loading ? '...' : (reportData?.total_orders || 0)}
                </p>
              </div>
              <div className="p-3 bg-indigo-50 rounded-lg">
                <ShoppingCart className="w-6 h-6 text-indigo-600" />
              </div>
            </div>
          </div>

          <div className="bg-white border border-slate-200 rounded-lg shadow-sm p-4 sm:p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-slate-500">Total Quantity</p>
                <p className="text-2xl sm:text-3xl font-bold text-slate-900 mt-1" data-testid="total-quantity">
                  {loading ? '...' : (reportData?.total_quantity || 0).toLocaleString()}
                </p>
              </div>
              <div className="p-3 bg-amber-50 rounded-lg">
                <Package className="w-6 h-6 text-amber-600" />
              </div>
            </div>
          </div>
        </div>

        {/* Orders Table */}
        <div className="bg-white border border-slate-200 rounded-lg shadow-sm overflow-hidden">
          <div className="p-4 sm:p-6 border-b border-slate-200">
            <h2 className="text-lg font-semibold text-slate-900">
              {reportData?.is_design_filtered ? 'Matching Line Items' : 'Matching Orders'}
            </h2>
            <p className="text-sm text-slate-500 mt-1">
              {loading ? 'Loading...' : reportData?.is_design_filtered 
                ? `${reportData?.line_items?.length || 0} line items from ${reportData?.total_orders || 0} orders`
                : `${reportData?.total_orders || 0} orders found`
              }
            </p>
          </div>

          {loading ? (
            <div className="text-center py-12">
              <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-indigo-600 mx-auto"></div>
              <p className="mt-4 text-slate-500">Loading report data...</p>
            </div>
          ) : (reportData?.is_design_filtered ? (reportData?.line_items?.length === 0) : (reportData?.orders?.length === 0)) ? (
            <div className="text-center py-12">
              <ShoppingCart className="w-12 h-12 text-slate-300 mx-auto mb-3" />
              <p className="text-slate-600 font-medium">No {reportData?.is_design_filtered ? 'line items' : 'orders'} found</p>
              <p className="text-sm text-slate-500 mt-1">
                Try adjusting your filters to see more results
              </p>
            </div>
          ) : reportData?.is_design_filtered ? (
            /* Design-filtered view - Line Items */
            <>
              {/* Mobile Card View - Line Items */}
              <div className="md:hidden divide-y divide-slate-200">
                {reportData?.line_items?.map((item, index) => (
                  <div key={`${item.order_number}-${item.design_number}-${index}`} className="p-4 space-y-2">
                    <div className="flex justify-between items-start">
                      <div>
                        <p className="font-semibold text-slate-900">{item.design_number}</p>
                        <p className="text-sm text-slate-600">{item.product_name}</p>
                      </div>
                      {getStatusBadge(item.status)}
                    </div>
                    <div className="text-xs text-slate-500">
                      Order: {item.order_number} • {item.customer_name}
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-slate-500">{formatISTDate(item.created_at)}</span>
                      <span className="font-medium text-slate-900">{formatCurrency(item.line_item_amount)}</span>
                    </div>
                    <div className="text-xs text-slate-500">
                      {item.quantity} pieces
                    </div>
                  </div>
                ))}
              </div>

              {/* Desktop Table View - Line Items */}
              <div className="hidden md:block overflow-x-auto">
                <table className="min-w-full divide-y divide-slate-200" data-testid="line-items-table">
                  <thead className="bg-slate-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Design Number</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Product Name</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Order Number</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Customer</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Date</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Status</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Quantity</th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-slate-500 uppercase tracking-wider">Line Amount</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-slate-200">
                    {reportData?.line_items?.map((item, index) => (
                      <tr key={`${item.order_number}-${item.design_number}-${index}`} className="hover:bg-slate-50 transition-colors">
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-indigo-600">
                          {item.design_number}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-900">
                          {item.product_name}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-600">
                          {item.order_number}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm font-medium text-slate-900">{item.customer_name}</div>
                          <div className="text-xs text-slate-500">{item.customer_email}</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-600">
                          {formatISTDate(item.created_at)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          {getStatusBadge(item.status)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-600">
                          {item.quantity} pcs
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-slate-900 text-right">
                          {formatCurrency(item.line_item_amount)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          ) : (
            /* Standard view - Orders */
            <>
              {/* Mobile Card View */}
              <div className="md:hidden divide-y divide-slate-200">
                {reportData?.orders?.map((order) => (
                  <div key={order.order_number} className="p-4 space-y-2">
                    <div className="flex justify-between items-start">
                      <div>
                        <p className="font-semibold text-slate-900">{order.order_number}</p>
                        <p className="text-sm text-slate-600">{order.customer_name}</p>
                      </div>
                      {getStatusBadge(order.status)}
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-slate-500">{formatISTDate(order.created_at)}</span>
                      <span className="font-medium text-slate-900">{formatCurrency(order.grand_total)}</span>
                    </div>
                    <div className="text-xs text-slate-500">
                      {order.total_quantity} pieces
                    </div>
                  </div>
                ))}
              </div>

              {/* Desktop Table View */}
              <div className="hidden md:block overflow-x-auto">
                <table className="min-w-full divide-y divide-slate-200" data-testid="orders-table">
                  <thead className="bg-slate-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Order Number</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Customer</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Date</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Status</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Quantity</th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-slate-500 uppercase tracking-wider">Amount</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-slate-200">
                    {reportData?.orders?.map((order) => (
                      <tr key={order.order_number} className="hover:bg-slate-50 transition-colors">
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-slate-900">
                          {order.order_number}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm font-medium text-slate-900">{order.customer_name}</div>
                          <div className="text-xs text-slate-500">{order.customer_email}</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-600">
                          {formatISTDate(order.created_at)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          {getStatusBadge(order.status)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-600">
                          {order.total_quantity} pcs
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-slate-900 text-right">
                          {formatCurrency(order.grand_total)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>
      </div>
    </Layout>
  );
};
